import React, { useContext, useState, useEffect, Fragment } from 'react'
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import DirectionsBusFilledIcon from '@mui/icons-material/DirectionsBusFilled';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import PushPromptBanner from './PushPromptBanner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { elUpperCase } from '../utils/text';
import { NAV_BG, AMBER, AMBER_DARK } from '../theme';

const ADMIN_NAV_PAGES = [
  'students', 'departments', 'map', 'parents',
  'drivers', 'teachers', 'routes', 'food', 'attendance', 'reports',
];

const TEACHER_NAV_PAGES = [
  'students', 'departments', 'parents', 'food', 'attendance', 'reports',
];

const DRIVER_NAV_PAGES = ['driver_map'];
const PARENT_NAV_PAGES = ['parent_today', 'parent_bus_tracking', 'parent_menu', 'parent_stats', 'parent_messages', 'parent_children'];

const NAV_PAGE_PATHS = {
  driver_map: '/driver/map',
  driver_settings: '/driver/settings',
  parent_today: '/parent/today',
  parent_bus_tracking: '/parent/bus-tracking',
  parent_menu: '/parent/menu',
  parent_stats: '/parent/stats',
  parent_messages: '/parent/messages',
  parent_children: '/parent/children',
  parent_settings: '/parent/settings',
};

const isMobileDevice = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;
const PWA_DISMISS_KEY = 'sb_pwa_install_dismissed';

const AMBER_GLOW = 'rgba(245, 158, 11, 0.25)';
const SURFACE = '#163571';
const BORDER = 'rgba(245, 158, 11, 0.22)';

function HeaderMUI() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  let { user, logoutUser, profile, schoolname, authTokens, backendUrl, userRole, subscribeToPush, getPushStatus } = useContext(AuthContext);
  const [pushStatus, setPushStatus] = useState(null);
  useEffect(() => {
    if (userRole === 0 || userRole === 1) {
      getPushStatus().then(setPushStatus);
    }
  }, [userRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showPWABadge, setShowPWABadge] = useState(() =>
    isMobileDevice() && !isStandaloneMode() && !!localStorage.getItem(PWA_DISMISS_KEY)
  );
  useEffect(() => {
    const onDismissed = () => {
      if (isMobileDevice() && !isStandaloneMode()) setShowPWABadge(true);
    };
    window.addEventListener('pwa-install-dismissed', onDismissed);
    return () => window.removeEventListener('pwa-install-dismissed', onDismissed);
  }, []);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  const NAV_PAGES = userRole === 2 ? DRIVER_NAV_PAGES : userRole === 1 ? PARENT_NAV_PAGES : userRole === 3 ? TEACHER_NAV_PAGES : ADMIN_NAV_PAGES;

  const getPagePath = (page) => NAV_PAGE_PATHS[page] || ('/' + page);
  const isActivePath = (page) => {
    const base = getPagePath(page);
    if (location.pathname === base) return true;
    if (page === 'students' && (location.pathname.startsWith('/student/') || location.pathname === '/add-student')) return true;
    if (page === 'parents' && location.pathname.startsWith('/add-parent')) return true;
    return false;
  };

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
    const first = NAV_PAGES[0];
    navigate(getPagePath(first), { replace: true });
  };
  const goToPage = (page) => { setAnchorElNav(null); navigate(getPagePath(page), { replace: true }); };
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleShowPWABanner = () => {
    localStorage.removeItem(PWA_DISMISS_KEY);
    setShowPWABadge(false);
    window.dispatchEvent(new CustomEvent('pwa-install-show'));
    handleCloseUserMenu();
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'el' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('sb_language', newLang);
  };

  useEffect(() => {
    if (!authTokens) return;
    if (userRole === 2) return; // drivers use parent_messages via different endpoint
    const endpoint = userRole === 1 ? 'api/messages/inbox/' : 'api/messages/received/';
    const fetchUnread = () => {
      fetch(backendUrl + endpoint, {
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + String(authTokens.access) },
      })
        .then(res => res.ok ? res.json() : [])
        .then(data => setUnreadMessages(Array.isArray(data) ? data.filter(m => !m.read).length : 0))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    window.addEventListener('messages-read', fetchUnread);
    return () => {
      clearInterval(interval);
      window.removeEventListener('messages-read', fetchUnread);
    };
  }, [authTokens, backendUrl, userRole]);

  if (user == null) return <></>;
  if (window.location.pathname === '/login') return null;

  const isParent = userRole === 1;

  const userInitials = user?.username ? user.username.slice(0, 2).toUpperCase() : 'U';
  const isActive = (page) => isActivePath(page);

  const navButtonStyles = (page) => ({
    px: 1.25,
    py: 0.6,
    borderRadius: '100px',
    fontFamily: '"Outfit", sans-serif',
    fontWeight: isActive(page) ? 600 : 500,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
    textTransform: 'none',
    minWidth: 0,
    color: isActive(page) ? NAV_BG : 'rgba(203, 213, 225, 0.85)',
    background: isActive(page)
      ? `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`
      : 'transparent',
    boxShadow: isActive(page) ? `0 2px 14px ${AMBER_GLOW}` : 'none',
    border: `1px solid ${isActive(page) ? 'transparent' : 'transparent'}`,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: isActive(page)
        ? `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`
        : 'rgba(245, 158, 11, 0.08)',
      color: isActive(page) ? NAV_BG : AMBER,
      boxShadow: isActive(page) ? `0 4px 18px ${AMBER_GLOW}` : `0 0 0 1px ${BORDER}`,
    },
  });

  return (
    <Fragment>
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: `linear-gradient(180deg, ${NAV_BG} 0%, #0c2550 100%)`,
        borderBottom: `1px solid ${BORDER}`,
        zIndex: 1200,
        paddingTop: 'var(--sat)',
        paddingLeft: 'var(--sal)',
        paddingRight: 'var(--sar)',
      }}
    >
      {/* Subtle top accent line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent 0%, ${AMBER} 40%, ${AMBER_DARK} 60%, transparent 100%)`,
          opacity: 0.7,
        }}
      />

      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ py: 0.75, minHeight: { xs: 56, md: 60 } }}>

          {/* ── Logo — Desktop ── */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', mr: 4, flexShrink: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                boxShadow: `0 0 18px ${AMBER_GLOW}`,
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              <DirectionsBusFilledIcon sx={{ color: '#080d1a', fontSize: 20 }} />
            </Box>
            {schoolname && (
              <Typography
                component="span"
                onClick={userRole === 0 || userRole === 3 ? () => navigate('/school', { replace: true }) : undefined}
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  letterSpacing: '0.015em',
                  color: '#f1f5f9',
                  cursor: userRole === 0 || userRole === 3 ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: userRole === 0 || userRole === 3 ? AMBER : '#f1f5f9' },
                }}
              >
                {schoolname}
              </Typography>
            )}
          </Box>

          {/* ── Hamburger — Mobile ── */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
            <IconButton size="large" onClick={handleOpenNavMenu} sx={{ color: AMBER, p: 1 }}>
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', lg: 'none' },
                '& .MuiPaper-root': {
                  background: SURFACE,
                  border: `1px solid ${BORDER}`,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                  borderRadius: '14px',
                  mt: 0.5,
                  minWidth: 180,
                },
              }}
            >
              {NAV_PAGES.map((page) => (
                <MenuItem
                  key={page}
                  onClick={() => goToPage(page)}
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: isActive(page) ? AMBER : '#cbd5e1',
                    mx: 0.5,
                    borderRadius: '8px',
                    my: 0.25,
                    '&:hover': { background: 'rgba(245,158,11,0.1)', color: AMBER },
                  }}
                >
                  {page === 'parent_messages' && unreadMessages > 0 ? (
                    <Badge badgeContent={unreadMessages} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                      <Box sx={{ pr: 2 }}>{t(`nav.${page}`)}</Box>
                    </Badge>
                  ) : t(`nav.${page}`)}
                </MenuItem>
              ))}
              {(userRole === 0 || userRole === 3) && (
                <MenuItem
                  onClick={() => { setAnchorElNav(null); navigate('/messages', { replace: true }); }}
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: location.pathname === '/messages' ? AMBER : '#cbd5e1',
                    mx: 0.5,
                    borderRadius: '8px',
                    my: 0.25,
                    '&:hover': { background: 'rgba(245,158,11,0.1)', color: AMBER },
                  }}
                >
                  {unreadMessages > 0 ? (
                    <Badge badgeContent={unreadMessages} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                      <Box sx={{ pr: 2 }}>{t('nav.messages')}</Box>
                    </Badge>
                  ) : t('nav.messages')}
                </MenuItem>
              )}
            </Menu>
          </Box>

          {/* ── Logo — Mobile ── */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', flexGrow: 1, ml: 0.5 }}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: '8px',
                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                mr: 1.25, flexShrink: 0,
              }}
            >
              <DirectionsBusFilledIcon sx={{ color: '#080d1a', fontSize: 17 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 700,
                fontSize: '0.95rem',
                color: '#f1f5f9',
              }}
            >
              {schoolname || 'SB2'}
            </Typography>
          </Box>

          {/* ── Desktop Nav ── */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0, overflow: 'hidden', minWidth: 0 }}>
            {NAV_PAGES.map((page) => (
              <Button key={page} onClick={() => goToPage(page)} sx={navButtonStyles(page)}>
                {page === 'parent_messages' && unreadMessages > 0 ? (
                  <Badge badgeContent={unreadMessages} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                    <Box sx={{ pr: 1.5 }}>{t(`nav.${page}`)}</Box>
                  </Badge>
                ) : t(`nav.${page}`)}
              </Button>
            ))}
          </Box>

          {/* ── Messages icon (admin/teacher only) ── */}
          {(userRole === 0 || userRole === 3) && (
            <Tooltip title={t('nav.messages')} arrow>
              <IconButton
                onClick={() => navigate('/messages', { replace: true })}
                sx={{
                  ml: 1,
                  color: location.pathname === '/messages' ? AMBER : 'rgba(203,213,225,0.8)',
                  '&:hover': { color: AMBER, background: 'rgba(245,158,11,0.08)' },
                }}
              >
                <Badge
                  badgeContent={unreadMessages > 0 ? unreadMessages : null}
                  color="error"
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}
                >
                  <MailOutlineIcon sx={{ fontSize: 22 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* ── Language toggle ── */}
          <Tooltip title={i18n.language === 'en' ? 'Switch to Greek' : 'Αλλαγή σε Αγγλικά'} arrow>
            <Button
              onClick={toggleLanguage}
              size="small"
              sx={{
                ml: 1,
                minWidth: 0,
                px: 1.25,
                py: 0.4,
                borderRadius: '8px',
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: 'rgba(203,213,225,0.8)',
                border: '1px solid rgba(245,158,11,0.25)',
                textTransform: 'none',
                letterSpacing: '0.03em',
                '&:hover': { background: 'rgba(245,158,11,0.08)', color: AMBER, borderColor: AMBER },
              }}
            >
              {i18n.language === 'en' ? 'ΕΛ' : 'EN'}
            </Button>
          </Tooltip>

          {/* ── User Avatar ── */}
          <Box sx={{ flexGrow: 0, ml: 1 }}>
            <Tooltip title={user?.username || 'Account'} arrow>
              <Badge
                badgeContent={showPWABadge ? '!' : null}
                sx={{
                  '& .MuiBadge-badge': {
                    background: '#ef4444',
                    color: '#fff',
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    minWidth: 16,
                    height: 16,
                    top: 4,
                    right: 4,
                  },
                }}
              >
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                      color: '#080d1a',
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      boxShadow: `0 0 10px ${AMBER_GLOW}`,
                      border: `1.5px solid rgba(245,158,11,0.4)`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: `0 0 18px rgba(245,158,11,0.5)`,
                        border: `1.5px solid rgba(245,158,11,0.7)`,
                      },
                    }}
                  >
                    {userInitials}
                  </Avatar>
                </IconButton>
              </Badge>
            </Tooltip>

            <Menu
              sx={{
                mt: '46px',
                '& .MuiPaper-root': {
                  background: SURFACE,
                  border: `1px solid ${BORDER}`,
                  boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
                  borderRadius: '14px',
                  minWidth: 190,
                  overflow: 'hidden',
                },
              }}
              anchorEl={anchorElUser}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {/* User info header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid rgba(245,158,11,0.12)`,
                  background: 'rgba(245,158,11,0.04)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    color: '#64748b',
                    letterSpacing: '0.08em',
                    mb: 0.25,
                  }}
                >
                  {elUpperCase(t('header.signed_in_as'))}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#f1f5f9',
                  }}
                >
                  {user?.username || 'User'}
                </Typography>
              </Box>

              {/* Preferences + Help (parent only) + Logout */}
              <Box sx={{ p: 0.75 }}>
                {showPWABadge && (
                  <MenuItem
                    onClick={handleShowPWABanner}
                    sx={{
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: AMBER,
                      borderRadius: '8px',
                      gap: 1,
                      mb: 0.5,
                      background: 'rgba(245,158,11,0.06)',
                      border: `1px solid rgba(245,158,11,0.2)`,
                      '&:hover': { background: 'rgba(245,158,11,0.14)', color: AMBER },
                    }}
                  >
                    <GetAppIcon sx={{ fontSize: 16 }} />
                    {t('pwa_install.install_app')}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => { handleCloseUserMenu(); navigate(userRole === 1 ? '/parent/settings' : userRole === 2 ? '/driver/settings' : '/preferences', { replace: true }); }}
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: '#cbd5e1',
                    borderRadius: '8px',
                    gap: 1,
                    mb: 0.25,
                    '&:hover': { background: 'rgba(245,158,11,0.08)', color: AMBER },
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 16 }} />
                  {t('header.preferences')}
                </MenuItem>
                {isParent && (
                  <MenuItem
                    onClick={() => { handleCloseUserMenu(); navigate('/parent/help', { replace: true }); }}
                    sx={{
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: '#cbd5e1',
                      borderRadius: '8px',
                      gap: 1,
                      mb: 0.25,
                      '&:hover': { background: 'rgba(245,158,11,0.08)', color: AMBER },
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: 16 }} />
                    {t('header.help')}
                  </MenuItem>
                )}
                {(userRole === 0 || userRole === 1) && pushStatus !== 'subscribed' && pushStatus !== 'unsupported' && (
                  <MenuItem
                    onClick={async () => {
                      handleCloseUserMenu();
                      const result = await subscribeToPush();
                      if (result === 'subscribed') setPushStatus('subscribed');
                    }}
                    sx={{
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: '#94a3b8',
                      borderRadius: '8px',
                      gap: 1,
                      mb: 0.25,
                      '&:hover': { background: 'rgba(99,179,237,0.1)', color: '#93c5fd' },
                    }}
                  >
                    <NotificationsNoneIcon sx={{ fontSize: 16 }} />
                    {t('header.enable_notifications')}
                  </MenuItem>
                )}
                {(userRole === 0 || userRole === 1) && pushStatus === 'subscribed' && (
                  <MenuItem disabled sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#4ade80', borderRadius: '8px', gap: 1, mb: 0.25 }}>
                    <NotificationsActiveIcon sx={{ fontSize: 16 }} />
                    {t('header.notifications_on')}
                  </MenuItem>
                )}
                {(userRole === 0 || userRole === 1) && !user?.is_premium && (
                  <MenuItem
                    component="a"
                    href="https://ko-fi.com/schoolbybus"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleCloseUserMenu}
                    sx={{
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: '#fb923c',
                      borderRadius: '8px',
                      gap: 1,
                      mb: 0.25,
                      '&:hover': { background: 'rgba(251,146,60,0.1)', color: '#fb923c' },
                    }}
                  >
                    <FavoriteBorderIcon sx={{ fontSize: 16 }} />
                    {t('header.donate')}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={logoutUser}
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: '#f87171',
                    borderRadius: '8px',
                    gap: 1,
                    '&:hover': { background: 'rgba(248,113,113,0.1)' },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 16 }} />
                  {t('header.logout')}
                </MenuItem>
              </Box>
            </Menu>
          </Box>

        </Toolbar>
      </Container>
    </AppBar>
    {(userRole === 0 || userRole === 1 || userRole === 2) && <PushPromptBanner />}
    </Fragment>
  );
}

export default HeaderMUI;
